<template>
  <AppHeaderLayout>
    <div class="page">
      <header class="page-header">
        <h1>帳號管理</h1>
        <button class="btn primary" type="button" @click="openCreate">新增帳號</button>
      </header>

      <section class="search-card">
        <div class="search-grid">
          <label class="form-field">
            <span>搜尋</span>
            <input
              v-model="search.keyword"
              type="text"
              placeholder="搜尋帳號、姓名、客戶名稱或 Mobile ID"
            />
          </label>
          <label class="form-field">
            <span>角色</span>
            <select v-model="search.role">
              <option value="">全部</option>
              <option value="A">管理者</option>
              <option value="C">客戶</option>
            </select>
          </label>
        </div>
      </section>

      <div class="table-card">
        <table>
          <thead>
            <tr>
              <th>帳號</th>
              <th>客戶名稱</th>
              <th>Email</th>
              <th>姓名</th>
              <th>角色</th>
              <th>Mobile</th>
              <th>啟用</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="account in pagedAccounts" :key="account.id">
              <td>{{ account.account }}</td>
              <td>{{ account.companyName }}</td>
              <td>{{ account.email }}</td>
              <td>{{ account.name }}</td>
              <td>{{ account.role }}</td>
              <td>{{ account.mobileIds && account.mobileIds.length ? account.mobileIds.join(', ') : '-' }}</td>
              <td>
                <label class="switch">
                  <input
                    type="checkbox"
                    :checked="account.active"
                    :disabled="isUpdatingEnableId === account.id"
                    @change="toggleActive(account)"
                  />
                  <span class="slider"></span>
                </label>
              </td>
              <td>
                <button class="link-button" type="button" @click="startEdit(account)">編輯</button>
              </td>
            </tr>
            <tr v-if="pagedAccounts.length === 0">
              <td colspan="8" class="empty">無資料</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer">
        <div class="page-size">
          <span>每頁顯示</span>
          <select v-model.number="pageSize">
            <option v-for="size in pageSizes" :key="size" :value="size">{{ size }}</option>
          </select>
          <span>筆</span>
          <span>共 {{ totalCount }} 筆</span>
        </div>

        <div class="pager">
          <button type="button" :disabled="!canPrev" @click="prevPage" aria-label="上一頁">&lt;</button>
          <span>{{ currentPage }} / {{ totalPages }}</span>
          <button type="button" :disabled="!canNext" @click="nextPage" aria-label="下一頁">&gt;</button>
        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-backdrop">
      <div class="modal">
        <div class="modal-header">
          <h2>{{ modalTitle }}</h2>
          <button class="modal-close" type="button" @click="closeModal">x</button>
        </div>
        <div class="modal-body">
          <div class="form-grid">
            <label class="form-field">
              <span>帳號</span>
              <input
                v-model="form.account"
                type="text"
                placeholder="請輸入帳號"
                :disabled="editingId !== null"
                maxlength="50"
                @input="onAccountInput"
              />
            </label>
            <label class="form-field">
              <span>客戶名稱</span>
              <select v-model.number="form.companyId">
                <option :value="0" disabled>請選擇客戶</option>
                <option v-for="company in companies" :key="company.id" :value="company.id">
                  {{ company.name }}
                </option>
              </select>
            </label>
            <label class="form-field">
              <span>Email</span>
              <input v-model="form.email" type="email" placeholder="請輸入 Email" />
            </label>
            <label class="form-field">
              <span>密碼</span>
              <input
                v-model="form.password"
                type="password"
                :placeholder="editingId !== null ? '*********' : '請輸入密碼'"
                :disabled="editingId !== null"
              />
            </label>
            <label class="form-field">
              <span>確認密碼</span>
              <input
                v-model="form.confirmPassword"
                type="password"
                :placeholder="editingId !== null ? '*********' : '請再次輸入密碼'"
                :disabled="editingId !== null"
              />
              <span v-if="passwordError" class="field-error">{{ passwordError }}</span>
            </label>
            <label class="form-field">
              <span>姓名</span>
              <input v-model="form.name" type="text" placeholder="請輸入姓名" />
            </label>
            <label class="form-field">
              <span>角色</span>
              <select v-model="form.role">
                <option value="" disabled>請選擇角色</option>
                <option value="A">管理者</option>
                <option value="C">客戶</option>
              </select>
            </label>
            <label class="form-field">
              <span>Mobile</span>
              <div class="mobile-dropdown">
                <button
                  class="mobile-trigger"
                  type="button"
                  @click="toggleMobileDropdown"
                  @click.stop
                >
                  {{ selectedMobileLabel }}
                </button>
                <div v-if="showMobileDropdown" class="mobile-panel" @click.stop>
                  <input
                    v-model.trim="mobileSearchKeyword"
                    class="mobile-search-input"
                    type="text"
                    placeholder="輸入 Mobile ID 篩選"
                    @click.stop
                  />
                  <label v-for="mobile in filteredMobileOptions" :key="mobile.id" class="mobile-item">
                    <input v-model="form.mobileRefIds" type="checkbox" :value="mobile.id" />
                    <span>{{ mobile.mobileId }}</span>
                  </label>
                  <div v-if="filteredMobileOptions.length === 0" class="mobile-empty">查無符合的 Mobile ID</div>
                </div>
              </div>
            </label>
          </div>
          <div v-if="optionLoadError" class="form-error">{{ optionLoadError }}</div>
        </div>
        <div class="modal-footer">
          <span v-if="formError" class="form-error">{{ formError }}</span>
          <div class="modal-actions">
            <button class="btn primary" type="button" :disabled="isSubmitting" @click="submitForm">
              {{ isSubmitting ? '儲存中...' : submitLabel }}
            </button>
            <button class="btn ghost" type="button" :disabled="isSubmitting" @click="closeModal">取消</button>
          </div>
        </div>
      </div>
    </div>
  </AppHeaderLayout>
</template>

<script>
import AppHeaderLayout from '@/components/AppHeaderLayout.vue';
import {
  ADD_MEMBER,
  MODIFY_MEMBER,
  GET_ALL_COMPANY,
  GET_ALL_MOBILE,
  GET_ALL_MEMBER,
  UPDATE_MEMBER_ENABLE
} from '@/utils/requestUrl';
import { getToken } from '@/utils/sessionStoraggeUtil';

export default {
  name: 'AccountManageView',
  components: {
    AppHeaderLayout
  },
  data() {
    return {
      companies: [],
      mobiles: [],
      accounts: [],
      form: {
        account: '',
        companyId: 0,
        email: '',
        password: '',
        confirmPassword: '',
        name: '',
        role: '',
        mobileRefIds: []
      },
      formError: '',
      passwordError: '',
      optionLoadError: '',
      search: {
        keyword: '',
        role: ''
      },
      showModal: false,
      showMobileDropdown: false,
      mobileSearchKeyword: '',
      editingId: null,
      pageSize: 10,
      pageSizes: [10, 50, 100],
      currentPage: 1,
      isSubmitting: false,
      isLoadingAccounts: false,
      isUpdatingEnableId: null,
      totalCount: 0,
      totalPagesFromApi: 0
    };
  },
  computed: {
    submitLabel() {
      return this.editingId === null ? '新增' : '儲存';
    },
    modalTitle() {
      return this.editingId === null ? '新增帳號' : '編輯帳號';
    },
    selectedMobileLabel() {
      const count = this.form.mobileRefIds.length;
      return count ? `已選擇 ${count} 筆 Mobile` : '請選擇 Mobile';
    },
    filteredMobileOptions() {
      const keyword = this.mobileSearchKeyword.trim().toLowerCase();
      if (!keyword) {
        return this.mobiles;
      }
      return this.mobiles.filter((mobile) => (mobile.mobileId || '').toLowerCase().includes(keyword));
    },
    filteredAccounts() {
      const keyword = this.search.keyword.trim().toLowerCase();
      if (!keyword) {
        return this.accounts;
      }
      return this.accounts.filter((account) =>
        account.companyName.toLowerCase().includes(keyword) ||
        account.name.toLowerCase().includes(keyword) ||
        account.account.toLowerCase().includes(keyword) ||
        (account.mobileIds || []).some((mobileId) => mobileId.toLowerCase().includes(keyword))
      );
    },
    totalPages() {
      return Math.max(1, this.totalPagesFromApi || 1);
    },
    pagedAccounts() {
      return this.filteredAccounts;
    },
    canPrev() {
      return this.currentPage > 1;
    },
    canNext() {
      return this.currentPage < this.totalPages;
    }
  },
  watch: {
    pageSize() {
      this.currentPage = 1;
      this.fetchAccounts();
    },
    'search.role'() {
      this.currentPage = 1;
      this.fetchAccounts();
    },
    'search.keyword'() {
      this.currentPage = 1;
    }
  },
  async created() {
    await Promise.all([this.fetchCompanies(), this.fetchMobiles()]);
    await this.fetchAccounts();
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    handleOutsideClick() {
      if (this.showMobileDropdown) {
        this.showMobileDropdown = false;
        this.mobileSearchKeyword = '';
      }
    },
    getApiBase() {
      if (window._env_ && window._env_.VUE_APP_REQUEST_DOMAIN) {
        return window._env_.VUE_APP_REQUEST_DOMAIN;
      }
      return '';
    },
    buildHeaders() {
      const token = getToken();
      if (!token) {
        throw new Error('登入已失效，請重新登入');
      }
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      };
    },
    handleUnauthorized(error) {
      const message = (error && error.message) || '';
      if (message.includes('登入已失效') || message.includes('401') || message.includes('Unauthorized')) {
        this.$router.push('/login');
      }
    },
    getUserMemberId() {
      const userInfo = this.$store.getters.getUserInfo || {};
      return userInfo.memberId || userInfo.MemberId || '';
    },
    mapCompany(item) {
      return {
        id: Number(item.companyID ?? item.CompanyID ?? 0),
        name: item.name || item.Name || ''
      };
    },
    mapMobile(item) {
      return {
        id: Number(item.id ?? item.Id ?? 0),
        mobileId: item.mobileId || item.MobileId || ''
      };
    },
    mapMemberTypeName(memberType) {
      return memberType === 'A' ? '管理者' : '客戶';
    },
    mapAccount(item) {
      const memberId = item.memberId || item.MemberId || '';
      const memberType = item.memberType || item.MemberType || '';
      const memberTypeName = item.memberTypeName || item.MemberTypeName || this.mapMemberTypeName(memberType);
      const mobileList = item.mobileList || item.MobileList || [];

      return {
        id: memberId,
        memberId,
        account: item.account || item.Account || '',
        companyId: Number(item.companyId ?? item.CompanyId ?? 0),
        companyName: item.companyName || item.CompanyName || '',
        email: item.email || item.Email || '',
        password: '',
        name: item.name || item.Name || '',
        role: memberTypeName,
        memberType,
        mobileIds: mobileList.map((m) => m.mobileId || m.MobileId || '').filter(Boolean),
        mobileRefIds: mobileList.map((m) => Number(m.mobileRefID ?? m.MobileRefID ?? 0)).filter((x) => x > 0),
        active: item.enable ?? item.Enable ?? false
      };
    },
    async fetchAccounts() {
      this.isLoadingAccounts = true;
      try {
        const response = await fetch(this.getApiBase() + GET_ALL_MEMBER, {
          method: 'POST',
          headers: this.buildHeaders(),
          body: JSON.stringify({
            PageIndex: this.currentPage,
            PageSize: this.pageSize,
            MemberType: this.search.role
          })
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        if (!response.ok || (payload && payload.status === 'failure')) {
          throw new Error((payload && payload.message) || `取得帳號資料失敗 (${response.status})`);
        }

        const data = (payload && payload.data) || {};
        const list = data.memberList || data.MemberList || [];

        this.accounts = list.map(this.mapAccount);
        this.totalCount = Number(data.totalCount ?? data.TotalCount ?? 0);
        this.totalPagesFromApi = Number(data.totalPages ?? data.TotalPages ?? 0);

        if (this.totalPagesFromApi > 0 && this.currentPage > this.totalPagesFromApi) {
          this.currentPage = this.totalPagesFromApi;
          await this.fetchAccounts();
        }
      } catch (error) {
        this.accounts = [];
        this.totalCount = 0;
        this.totalPagesFromApi = 0;
        this.optionLoadError = error.message || '取得帳號資料失敗';
        this.handleUnauthorized(error);
      } finally {
        this.isLoadingAccounts = false;
      }
    },
    async fetchCompanies() {
      try {
        const response = await fetch(this.getApiBase() + GET_ALL_COMPANY, {
          method: 'POST',
          headers: this.buildHeaders(),
          body: JSON.stringify({
            PageIndex: 1,
            PageSize: 200
          })
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        if (!response.ok || (payload && payload.status === 'failure')) {
          throw new Error((payload && payload.message) || `取得客戶資料失敗 (${response.status})`);
        }

        const data = (payload && payload.data) || {};
        const list = data.companyList || data.CompanyList || [];
        this.companies = list.map(this.mapCompany);
      } catch (error) {
        this.companies = [];
        this.optionLoadError = error.message || '取得客戶資料失敗';
        this.handleUnauthorized(error);
      }
    },
    async fetchMobiles() {
      try {
        const response = await fetch(this.getApiBase() + GET_ALL_MOBILE, {
          method: 'POST',
          headers: this.buildHeaders(),
          body: JSON.stringify({
            PageIndex: 1,
            PageSize: 200
          })
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        if (!response.ok || (payload && payload.status === 'failure')) {
          throw new Error((payload && payload.message) || `取得 Mobile 資料失敗 (${response.status})`);
        }

        const data = (payload && payload.data) || {};
        const list = data.mobileList || data.MobileList || [];
        this.mobiles = list.map(this.mapMobile);
      } catch (error) {
        this.mobiles = [];
        this.optionLoadError = error.message || '取得 Mobile 資料失敗';
        this.handleUnauthorized(error);
      }
    },
    toggleMobileDropdown() {
      this.showMobileDropdown = !this.showMobileDropdown;
      if (!this.showMobileDropdown) {
        this.mobileSearchKeyword = '';
      }
    },
    openCreate() {
      this.resetForm();
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.resetForm();
    },
    sanitizeAccount(value) {
      return (value || '').replace(/[^A-Za-z0-9]/g, '');
    },
    onAccountInput() {
      this.form.account = this.sanitizeAccount(this.form.account);
    },
    async submitForm() {
      this.formError = '';
      this.passwordError = '';

      if (!this.form.companyId || !this.form.email || !this.form.name || !this.form.role) {
        this.formError = '請完整輸入必填欄位';
        return;
      }

      const email = (this.form.email || '').trim();
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(email)) {
        this.formError = 'Email 格式不正確';
        return;
      }
      this.form.email = email;

      if (this.editingId === null) {
        this.form.account = this.sanitizeAccount(this.form.account);

        if (!this.form.account || !this.form.password || !this.form.confirmPassword) {
          this.formError = '請完整輸入必填欄位';
          return;
        }

        if (!/^[A-Za-z0-9]+$/.test(this.form.account)) {
          this.formError = '帳號只能輸入英文與數字';
          return;
        }

        if (this.form.password !== this.form.confirmPassword) {
          this.passwordError = '密碼與確認密碼不一致';
          return;
        }
      }

      const creator = this.getUserMemberId();
      if (!creator) {
        this.formError = '無法取得登入者資訊，請重新登入後再試';
        return;
      }

      this.isSubmitting = true;
      try {
        const memberType = this.form.role || 'C';
        const isEdit = this.editingId !== null;
        const apiPath = isEdit ? MODIFY_MEMBER : ADD_MEMBER;
        const requestBody = isEdit
          ? {
              MemberID: this.editingId,
              Email: this.form.email,
              Name: this.form.name,
              MemberType: memberType,
              CompanyID: this.form.companyId,
              MobileRefIds: this.form.mobileRefIds,
              Updator: creator
            }
          : {
              Account: this.form.account,
              Password: this.form.password,
              Email: this.form.email,
              Name: this.form.name,
              MemberType: memberType,
              CompanyID: this.form.companyId,
              MobileRefIds: this.form.mobileRefIds,
              Creator: creator
            };

        const response = await fetch(this.getApiBase() + apiPath, {
          method: 'POST',
          headers: this.buildHeaders(),
          body: JSON.stringify(requestBody)
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        if (!response.ok || (payload && payload.status === 'failure')) {
          throw new Error((payload && payload.message) || `儲存帳號失敗 (${response.status})`);
        }

        this.currentPage = 1;
        await this.fetchAccounts();
        this.closeModal();
      } catch (error) {
        this.formError = error.message || '儲存帳號失敗';
        this.handleUnauthorized(error);
      } finally {
        this.isSubmitting = false;
      }
    },
    startEdit(account) {
      this.editingId = account.memberId || account.id;
      this.form.account = account.account;
      this.form.companyId = account.companyId;
      this.form.email = account.email;
      this.form.password = account.password;
      this.form.confirmPassword = account.password;
      this.form.name = account.name;
      this.form.role = account.memberType || 'C';
      this.form.mobileRefIds = account.mobileRefIds && account.mobileRefIds.length
        ? [...account.mobileRefIds]
        : this.mobiles
            .filter((m) => (account.mobileIds || []).includes(m.mobileId))
            .map((m) => m.id);
      this.formError = '';
      this.passwordError = '';
      this.showModal = true;
      this.showMobileDropdown = false;
      this.mobileSearchKeyword = '';
    },
    resetForm() {
      this.editingId = null;
      this.form.account = '';
      this.form.companyId = 0;
      this.form.email = '';
      this.form.password = '';
      this.form.confirmPassword = '';
      this.form.name = '';
      this.form.role = '';
      this.form.mobileRefIds = [];
      this.formError = '';
      this.passwordError = '';
      this.showMobileDropdown = false;
      this.mobileSearchKeyword = '';
    },
    async toggleActive(account) {
      const updator = this.getUserMemberId();
      if (!updator) {
        this.optionLoadError = '無法取得登入者資訊，請重新登入後再試';
        return;
      }

      const newEnable = !account.active;
      this.isUpdatingEnableId = account.id;
      try {
        const response = await fetch(this.getApiBase() + UPDATE_MEMBER_ENABLE, {
          method: 'POST',
          headers: this.buildHeaders(),
          body: JSON.stringify({
            MemberID: account.memberId || account.id,
            Enable: newEnable,
            Updator: updator
          })
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        if (!response.ok || (payload && payload.status === 'failure')) {
          throw new Error((payload && payload.message) || `更新啟用狀態失敗 (${response.status})`);
        }

        account.active = newEnable;
      } catch (error) {
        this.optionLoadError = error.message || '更新啟用狀態失敗';
        this.handleUnauthorized(error);
      } finally {
        this.isUpdatingEnableId = null;
      }
    },
    async prevPage() {
      if (this.canPrev) {
        this.currentPage -= 1;
        await this.fetchAccounts();
      }
    },
    async nextPage() {
      if (this.canNext) {
        this.currentPage += 1;
        await this.fetchAccounts();
      }
    }
  }
};
</script>

<style scoped>
.page {
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 12px 24px rgba(15, 30, 40, 0.08);
  font-size: 1rem;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.page-header h1 {
  margin: 0;
  font-size: 1.625rem;
}

.search-card {
  border-radius: 14px;
  border: 1px solid rgba(15, 30, 40, 0.08);
  padding: 16px;
  margin-bottom: 20px;
  background: #ffffff;
}

.search-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.9375rem;
}

.form-field input,
.form-field select {
  height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(20, 40, 50, 0.2);
  padding: 0 12px;
  font-size: 0.9375rem;
}

.form-field input:focus,
.form-field select:focus {
  outline: none;
  border-color: rgba(10, 160, 165, 0.7);
  box-shadow: 0 0 0 3px rgba(10, 160, 165, 0.15);
}

.field-error {
  color: #b91c1c;
  font-size: 0.8125rem;
}

.form-error {
  color: #b91c1c;
  font-size: 0.875rem;
}

.mobile-dropdown {
  position: relative;
}

.mobile-trigger {
  width: 100%;
  height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(20, 40, 50, 0.2);
  padding: 0 12px;
  font-size: 0.9375rem;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
}

.mobile-panel {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 6px);
  border-radius: 10px;
  border: 1px solid rgba(20, 40, 50, 0.2);
  background: #ffffff;
  max-height: 180px;
  overflow-y: auto;
  padding: 10px 12px;
  z-index: 2;
  box-shadow: 0 12px 24px rgba(15, 30, 40, 0.12);
}

.mobile-search-input {
  width: calc(100% - 8px);
  box-sizing: border-box;
  height: 36px;
  border-radius: 8px;
  border: 1px solid rgba(20, 40, 50, 0.2);
  padding: 0 10px;
  font-size: 0.875rem;
  margin: 0 4px 10px 0;
}

.mobile-search-input:focus {
  outline: none;
  border-color: rgba(10, 160, 165, 0.7);
  box-shadow: 0 0 0 3px rgba(10, 160, 165, 0.15);
}

.mobile-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  padding: 6px 0;
}

.mobile-empty {
  color: rgba(31, 42, 51, 0.55);
  font-size: 0.875rem;
  padding: 6px 0 2px;
}

.btn {
  min-width: 90px;
  height: 36px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-size: 0.9375rem;
}

.btn.primary {
  background: #0aa0a5;
  color: #ffffff;
}

.btn.ghost {
  background: #eef2f4;
  color: #1f2a33;
}

.table-card {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(15, 30, 40, 0.08);
}

.table-card table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9375rem;
}

.table-card thead {
  background: #f4f7f8;
  text-align: left;
}

.table-card th,
.table-card td {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(15, 30, 40, 0.08);
}

.table-card tbody tr:hover {
  background: rgba(10, 160, 165, 0.06);
}

.link-button {
  border: none;
  background: transparent;
  color: #0aa0a5;
  cursor: pointer;
  padding: 0;
  font-size: 0.9375rem;
}

.link-button:hover {
  text-decoration: underline;
}

.switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: #d1d6d9;
  transition: 0.2s;
  border-radius: 999px;
}

.slider:before {
  position: absolute;
  content: '';
  height: 18px;
  width: 18px;
  left: 2px;
  top: 2px;
  background-color: white;
  transition: 0.2s;
  border-radius: 50%;
}

.switch input:checked + .slider {
  background-color: #0aa0a5;
}

.switch input:checked + .slider:before {
  transform: translateX(18px);
}

.empty {
  text-align: center;
  padding: 24px 0;
  color: rgba(31, 42, 51, 0.5);
}

.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 18px;
  gap: 16px;
  flex-wrap: wrap;
}

.page-size {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9375rem;
}

.page-size select {
  border-radius: 8px;
  border: 1px solid rgba(15, 30, 40, 0.2);
  padding: 4px 8px;
  font-size: 0.9375rem;
}

.pager {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9375rem;
}

.pager button {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(15, 30, 40, 0.2);
  background: #ffffff;
  cursor: pointer;
}

.pager button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 16, 20, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.modal {
  width: min(720px, 92vw);
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 48px rgba(15, 30, 40, 0.2);
  padding: 20px 22px 18px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.375rem;
}

.modal-close {
  border: none;
  background: transparent;
  font-size: 1.625rem;
  cursor: pointer;
  line-height: 1;
}

.modal-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 18px;
  flex-wrap: wrap;
}

.modal-actions {
  display: flex;
  gap: 10px;
}

.modal .form-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

@media (max-width: 960px) {
  .modal .form-grid,
  .search-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .page {
    padding: 20px;
  }

  .modal .form-grid,
  .search-grid {
    grid-template-columns: 1fr;
  }

  .table-footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>




